/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import { useState, useMemo, useCallback, useEffect } from "react";

// Stable empty array to prevent infinite re-renders
const EMPTY_ARRAY: any[] = [];
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";

import { AdvancedLeaderboardFilters, CustomDateRange } from "@/app/(authenticated)/leaderboard/_modules/components/AdvancedLeaderboardFilters";
import { HeroPodium } from "@/app/(authenticated)/leaderboard/_modules/components/HeroPodium";
import { LeaderboardHeader } from "@/app/(authenticated)/leaderboard/_modules/components/LeaderboardHeader";
import { SimpleLeaderboardTable } from "@/app/(authenticated)/leaderboard/_modules/components/SimpleLeaderboardTable";
import { useRealtimeLeaderboard } from "./_modules/hooks/use-realtime-leaderboard";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import { EyeOffIcon, Settings, RefreshCw, Share2 } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import Link from "next/link";
import useAgencyStatus, { AGENCY_STATUS_QUERY_KEY } from "@/app/_module/hooks/useAgencyStatus";
import AgencyCreationStep from "@/app/auth/sign-up/_modules/components/AgencyCreationStep";

import { PerformanceCards } from "./_modules/components/PerformanceCards";
import { ShareWinDialog, type ShareMe, type ShareTop3 } from "./_modules/components/ShareWinDialog";
import { useLeaderboard } from "./_modules/hooks/use-hook";
import { useAgencyTree } from "./_modules/hooks/use-agency-tree";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useQuery } from "@tanstack/react-query";
import { endpoint } from "@/app/_module/services/endpoints";
import { getApiUrl } from "@/app/_module/services/config";
import api from "@/app/_module/services/api";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

// localStorage key for leaderboard filters persistence
// Versioned: every existing user has a saved metric of 'total-premium' from
// before Total Premium Sold existed, and a stored value beats the default — so
// without bumping the key the new default would reach nobody.
const LEADERBOARD_FILTERS_KEY = 'leaderboard-filters-v2';

// Helper to safely parse stored filters from localStorage
const getStoredFilters = (): { period: string; metric: string; customDateRange?: CustomDateRange } => {
  if (typeof window === 'undefined') return { period: 'this-month', metric: 'total-premium-sold' };
  try {
    const stored = localStorage.getItem(LEADERBOARD_FILTERS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert date strings back to Date objects if custom range exists
      if (parsed.customDateRange?.from && parsed.customDateRange?.to) {
        parsed.customDateRange = {
          from: new Date(parsed.customDateRange.from),
          to: new Date(parsed.customDateRange.to),
        };
      }
      return parsed;
    }
  } catch {
    // Ignore parse errors
  }
  return { period: 'this-month', metric: 'total-premium-sold' };
};

const AgentLeaderboard = function () {
  // ALL HOOKS MUST BE CALLED FIRST - BEFORE ANY EARLY RETURNS
  const { user, isInitializing } = useUserContext();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: agencyStatus, isLoading: statusLoading } = useAgencyStatus();

  // Initialize state from localStorage (if available)
  const [selectedPeriod, setSelectedPeriod] = useState(() => getStoredFilters().period);
  const [selectedMetric, setSelectedMetric] = useState(() => getStoredFilters().metric);
  const [searchQuery, setSearchQuery] = useState("");
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange | undefined>(() => getStoredFilters().customDateRange);
  const [selectedLeaderboardAgencyId, setSelectedLeaderboardAgencyId] = useState<string | undefined>(undefined);

  // Persist filter changes to localStorage
  // Skip saving when custom period has no dates yet (user is still picking)
  useEffect(() => {
    if (selectedPeriod === 'custom' && !customDateRange) return;
    const filters = {
      period: selectedPeriod,
      metric: selectedMetric,
      ...(customDateRange ? { customDateRange: { from: customDateRange.from.toISOString(), to: customDateRange.to.toISOString() } } : {}),
      ...(selectedLeaderboardAgencyId ? { selectedAgencyId: selectedLeaderboardAgencyId } : {}),
    };
    localStorage.setItem(LEADERBOARD_FILTERS_KEY, JSON.stringify(filters));
  }, [selectedPeriod, selectedMetric, customDateRange, selectedLeaderboardAgencyId]);

  // Convert kebab-case to camelCase for API - do this BEFORE early returns but AFTER hooks
  // kebab -> camel, except 'total-premium-sold' whose backend field is
  // `issuedPremium`, not `totalPremiumSold`. Without this the API gets an
  // unknown metric and silently falls back to its default sort.
  const apiMetric =
    selectedMetric === 'total-premium-sold'
      ? 'issuedPremium'
      : selectedMetric.replace(/-([a-z])/g, (g) => g[1].toUpperCase());

  // Extract stable primitive values for dependencies BEFORE any conditional logic
  // This ensures we have stable values for useMemo dependencies
  // Use agencyStatus.agencyId as fallback when user context hasn't hydrated yet
  const currentAgencyId = user?.currentAgency?.agency?.id || agencyStatus?.agencyId;
  const userType = user?.user?.userType;
  const userId = user?.user?.id; // Extract user ID for stable dependency


  // Agency switcher: only owners and system admins can switch agencies
  // Agents (role 'agent') should NOT see the agency switcher even if their userType is 'agency_owner'
  // Uses multiple signals to detect ownership — role from context/API + isOwner boolean from agencyStatus.
  // This ensures the switcher shows as soon as ANY data source confirms ownership, avoiding the
  // "need to refresh multiple times" issue where async data loads at different speeds.
  // ROLE PRECEDENCE FLIP (2026-07-23): agencyStatus.role is authoritative —
  // it's fetched per current agency (keyed query + agency-change invalidation)
  // while user.currentAgency.role is a login-flow snapshot that can lag one
  // agency behind after the login agency-select (showed the "Switch to Your
  // Own Agency" gate on the agency the user OWNS until hard refresh).
  const agencyRole = (agencyStatus?.role || user?.currentAgency?.role || '').toLowerCase();
  const isAgencyOwner = agencyRole === 'owner' || agencyStatus?.isOwner === true;
  const canSwitchAgencies = userType === 'admin' || (isAgencyOwner && !!currentAgencyId);
  const { data: agencyTree } = useAgencyTree(canSwitchAgencies ? currentAgencyId : undefined);

  // Compute effective agency ID for leaderboard queries
  const effectiveAgencyId = selectedLeaderboardAgencyId || currentAgencyId;

  // Reset selected agency when current agency changes (e.g. user switches agency via main nav)
  useEffect(() => {
    setSelectedLeaderboardAgencyId(undefined);
  }, [currentAgencyId]);

  // Restore persisted agency selection on mount
  useEffect(() => {
    if (canSwitchAgencies && agencyTree && agencyTree.length > 1) {
      const stored = getStoredFilters() as any;
      if (stored.selectedAgencyId && agencyTree.some((a: any) => a.id === stored.selectedAgencyId)) {
        setSelectedLeaderboardAgencyId(stored.selectedAgencyId);
      }
    }
  }, [canSwitchAgencies, agencyTree]);

  // Fetch user's visibility settings to show "You are hidden" banner
  const { data: visibilityData } = useQuery({
    queryKey: ['leaderboard-visibility'],
    queryFn: async () => {
      return await handleApiCall<{ data: { showOnLeaderboard: boolean } }>(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.leaderboard.getUserVisibilitySettings),
        });
        return response;
      });
    },
    staleTime: 0, // Always refetch to get latest visibility status
    refetchOnMount: 'always', // Force refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when user returns to window
  });

  // Get visibility - prioritize user context since it's updated immediately when settings change
  // The API response may be stale due to caching, but user context is always fresh
  // Priority: user context (updated on settings save) > API response > default true
  const userOptedIn = user?.user?.showOnLeaderboard ?? visibilityData?.data?.showOnLeaderboard ?? true;

  // Check agency leaderboard settings (only for agency members)
  // Returns the full settings shape including parentGodmodeEnabled so the
  // scope picker knows whether to expose the "Full downline" option.
  const { data: agencyLeaderboardSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['agency-leaderboard-settings', currentAgencyId],
    queryFn: async () => {
      if (!currentAgencyId) return { leaderboardEnabled: true, parentGodmodeEnabled: false }; // Independent agents default to enabled

      try {
        const response = await handleApiCall<{ leaderboardEnabled: boolean; parentGodmodeEnabled?: boolean }>(async () => {
          return await api.get({
            url: getApiUrl(endpoint.agency.getLeaderboardSettings(currentAgencyId)),
            auth: true,
          });
        });
        return response || { leaderboardEnabled: true, parentGodmodeEnabled: false }; // Default to enabled if not set
      } catch (error) {
        // If endpoint doesn't exist yet, default to enabled

        return { leaderboardEnabled: true, parentGodmodeEnabled: false };
      }
    },
    enabled: !!currentAgencyId, // Only fetch for agency members
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
  });

  // Session-only god-mode flag. Persists across re-renders but resets on
  // page reload — by design (god-mode is a "I am about to look" action,
  // not a default view). Only honored when the agency has
  // parentGodmodeEnabled=true; backend will silently fall back otherwise.
  const parentGodmodeAvailable = agencyLeaderboardSettings?.parentGodmodeEnabled === true;
  const [isGodmodeActive, setIsGodmodeActive] = useState(false);

  // Per-person view scope. When the parent force-merges everyone into one
  // agency-wide board, this lets each person narrow what they see — purely a
  // personal view, it never changes the setting for anyone else:
  //   'mine'     → just their own agency's agents
  //   'downline' → their agency + everything below it (owners with sub-agencies)
  //   'wide'     → the full agency-wide pool the parent set up
  // Default 'mine' so people land on their own team and aren't confused.
  // Default to the widest view the agency allows: the point of turning agency-wide on is that
  // people see the whole organisation. The page used to start on "My Agency" every load, so a
  // sub-agency's agents never saw anyone else unless they clicked the toggle each time.
  // The last choice is remembered per browser.
  const [viewScope, setViewScopeState] = useState<'mine' | 'downline' | 'wide'>('wide');
  useEffect(() => {
    // Read after mount so the server and first client paint agree.
    try {
      const saved = window.localStorage.getItem('leaderboard:viewScope');
      if (saved === 'mine' || saved === 'downline' || saved === 'wide') setViewScopeState(saved);
    } catch {}
  }, []);
  const setViewScope = (scope: 'mine' | 'downline' | 'wide') => {
    setViewScopeState(scope);
    try { window.localStorage.setItem('leaderboard:viewScope', scope); } catch {}
  };

  // Check agency-wide settings. effectiveAgencyWide is true whenever this agent
  // is actually viewing a merged multi-agency board — including when a parent
  // force-merges them in (force_sub_agency_inclusion) despite their own flag
  // being off. We gate the "My Agency" toggle on this, not the own-agency flag.
  const { data: agencyWideSettings } = useQuery({
    queryKey: ['agency-wide-settings', currentAgencyId],
    queryFn: async () => {
      if (!currentAgencyId) return { agencyWideEnabled: false, effectiveAgencyWide: false };
      try {
        const response = await handleApiCall<{ agencyWideEnabled: boolean; effectiveAgencyWide?: boolean }>(async () => {
          return await api.get({
            url: getApiUrl(endpoint.agency.getAgencyWideSettings(currentAgencyId)),
            auth: true,
          });
        });
        return response || { agencyWideEnabled: false, effectiveAgencyWide: false };
      } catch {
        return { agencyWideEnabled: false, effectiveAgencyWide: false };
      }
    },
    enabled: !!currentAgencyId,
    staleTime: 5 * 60 * 1000,
  });

  const isLeaderboardEnabled = agencyLeaderboardSettings?.leaderboardEnabled ?? true;

  // Resolve the effective scope. The "My Agency" toggle only applies when the
  // parent enabled the agency-wide *setting* (the everyone-in-one case). God-mode
  // is a deliberate owner action, so we never let the toggle narrow it.
  // Use the effective flag so force-merged agents (parent forced them in) also
  // get the toggle, not just agencies that flipped their own agency-wide on.
  const agencyWideSettingOn =
    agencyWideSettings?.effectiveAgencyWide === true ||
    agencyWideSettings?.agencyWideEnabled === true;
  const godmodeOn = parentGodmodeAvailable && isGodmodeActive;
  // Everyone merged into the agency-wide board gets the scope picker — agents
  // AND sub-agency owners (e.g. Brandon) who were force-merged in and want to
  // see just their own team. We don't show it while Full Downline (god-mode) is
  // active, since that's a deliberate "see everything below me" view.
  const canChooseScope = agencyWideSettingOn && !godmodeOn;
  // Owners with sub-agencies also get a "My Sub-Agencies" middle option (their
  // own org, not the whole parent agency-wide pool). agencyTree is only fetched
  // for owners/admins; >1 node means they have a downline.
  const hasDownline = isAgencyOwner && (agencyTree?.length ?? 0) > 1;
  const showDownlineOption = canChooseScope && hasDownline;
  // If the downline option isn't available, never let scope sit on 'downline'.
  // 'wide' with the setting off is just the normal board; 'downline' without a downline falls back to mine.
  const effectiveScope = viewScope === 'downline' && !showDownlineOption ? 'mine' : viewScope;
  const viewingMyAgencyOnly = canChooseScope && effectiveScope === 'mine';
  const viewingDownlineOnly = showDownlineOption && effectiveScope === 'downline';

  // Pause auto-refresh while user is picking custom dates (prevents re-renders closing the calendar)
  const isCustomWithoutDates = selectedPeriod === 'custom' && !customDateRange;

  // Fetch leaderboard data - MUST be called before any conditional returns
  const {
    data: leaderboardData,
    loading,
    isRefreshing,
    error,
    refetch,
    manualRefresh,
    canRefresh,
    cachedAt,
    updateVisibility,
  } = useLeaderboard({

    primaryMetric: apiMetric,
    period: selectedPeriod,
    visibleOnly: true,
    autoRefresh: !isCustomWithoutDates,
    // Backend caches at 60s TTL. Poll every 60s so we pull fresh data right after
    // the cache expires — any faster just hits Redis with stale data.
    refreshInterval: 60_000,
    customDateRange: selectedPeriod === 'custom' ? customDateRange : undefined,
    agencyIdOverride: effectiveAgencyId,
    requestGodmode: godmodeOn,
    myAgencyOnly: viewingMyAgencyOnly,
    myDownlineOnly: viewingDownlineOnly,
  });

  // ── Share-your-win dialog (agent rank card / owner top-3 card) ──────────
  const [shareOpen, setShareOpen] = useState(false);
  // The board actually being VIEWED — an owner browsing a sub-agency's board
  // shares cards named for THAT sub-agency, not their own org.
  const effectiveAgencyName = useMemo(
    () =>
      (selectedLeaderboardAgencyId && agencyTree
        ? agencyTree.find((a: { id: string; name?: string }) => a.id === selectedLeaderboardAgencyId)?.name
        : undefined) || user?.currentAgency?.agency?.name,
    [selectedLeaderboardAgencyId, agencyTree, user?.currentAgency?.agency?.name],
  );
  const shareMe: ShareMe | null = useMemo(() => {
    const rows = leaderboardData?.leaderboard;
    if (!userId || !rows?.length) return null;
    const mine = rows.find((r) => r.userId === userId);
    if (!mine) return null;
    return {
      name: mine.name,
      rank: mine.rank,
      count: Number((mine as { totalPolicies?: number }).totalPolicies ?? mine.totalSales ?? 0),
      premiumSold: mine.issuedPremium
        ? `$${Math.round(mine.issuedPremium).toLocaleString()}`
        : undefined,
      agencyName: effectiveAgencyName,
      photoUrl: (mine as { profileImageUrl?: string }).profileImageUrl,
    };
  }, [leaderboardData, userId, effectiveAgencyName]);
  const shareTop3: ShareTop3 | null = useMemo(() => {
    const rows = leaderboardData?.leaderboard;
    // Team cards are for people who LEAD a team. `isAgencyOwner` alone proved
    // brittle: it hinges on agencyStatus, which is undefined while loading and
    // on any fetch error, and on a role string that reflects the CURRENTLY
    // SELECTED agency (an owner browsing an agency where they're a 'viewer'
    // reads as a non-owner). ownedAgencyId is the durable signal — the backend
    // always returns it for anyone who owns an agency anywhere — and userType
    // covers the login-snapshot case. Any one of them is enough.
    const leadsATeam =
      isAgencyOwner ||
      !!agencyStatus?.ownedAgencyId ||
      String(userType || '').toLowerCase() === 'agency_owner';
    if (!leadsATeam || !rows?.length) return null;
    return {
      agencyName: effectiveAgencyName || "Our agency",
      // Top 10 — the podium card uses the first 3, the top-ten grid all 10.
      rows: [...rows]
        .sort((a, b) => (a.rank || 999) - (b.rank || 999))
        .slice(0, 10)
        .map((r) => ({
          name: r.name,
          value: r.issuedPremium
            ? `$${Math.round(r.issuedPremium).toLocaleString()}`
            : String((r as { totalPolicies?: number }).totalPolicies ?? r.totalSales ?? 0),
          count: Number((r as { totalPolicies?: number }).totalPolicies ?? r.totalSales ?? 0) || undefined,
          photoUrl: (r as { profileImageUrl?: string }).profileImageUrl,
        })),
      // The recruiting flex: team total across the WHOLE board + writer count.
      totalPremium: Math.round(rows.reduce((s, r) => s + (r.issuedPremium || 0), 0)) || undefined,
      writers: rows.length,
    };
  }, [leaderboardData, isAgencyOwner, agencyStatus?.ownedAgencyId, userType, effectiveAgencyName]);
  const sharePeriodLabel = useMemo(() => {
    const raw = String(selectedPeriod || "this month").replace(/[-_]/g, " ");
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }, [selectedPeriod]);


  // Real-time updates: Aggressive polling with optional SSE support
  // This ensures leaderboard updates quickly when new policies are created
  // Only enabled for agency members (not independent agents viewing global leaderboard)
  // Disabled while picking custom dates to prevent re-renders closing the calendar popover
  useRealtimeLeaderboard({
    enabled: !!currentAgencyId && !isCustomWithoutDates, // Pause during date selection
    agencyId: effectiveAgencyId,
    fallbackPollingInterval: 10000, // Poll every 10 seconds as fallback
  });

  // ALL USEMEMO HOOKS MUST BE CALLED BEFORE EARLY RETURNS
  // This is critical for React's Rules of Hooks
  const baseTransformedAgents = useMemo(() => {
    // Add userId check to prevent errors during initialization
    if (!userId || !leaderboardData?.leaderboard || leaderboardData.leaderboard.length === 0) return EMPTY_ARRAY;

    // Use stable userType from dependency instead of extracting from user object
    const isSystemAdmin = userType === 'admin';

    // Use stable hash for random values based on current user's userId + agent's userId to prevent infinite re-renders
    const getStableRandom = (agentUserId: string, seed: number) => {
      let hash = 0;
      const str = agentUserId + userId + seed; // Combine agent ID with current user ID for uniqueness
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash) / 2147483647; // Normalize to 0-1
    };

    const mappedAgents = leaderboardData.leaderboard
      .map((agent, index) => ({
        // Required LeaderboardAgent properties
        userId: agent.userId,
        name: agent.name,
        department: agent.department,
        agency: agent.agency,
        subAgency: agent.subAgency,
        totalPremium: agent.totalPremium,
        issuedPremium: agent.issuedPremium ?? 0,
        totalSales: agent.totalSales,
        conversionRate: agent.conversionRate,
        wonLeads: agent.wonLeads,
        lostLeads: agent.lostLeads,
        underwritingLeads: agent.underwritingLeads,
        goalProgress: agent.goalProgress ?? null,
        goalLabel: agent.goalLabel ?? null,
        leadsConverted: agent.leadsConverted,
        appointments: agent.appointments,
        callsMade: agent.callsMade,
        activeClients: agent.activeClients,
        visibility: agent.visibility,
        rank: agent.rank,
        // Additional properties for UI
        id: parseInt(agent.userId) || index,
        showInLeaderboard: agent.showInLeaderboard ?? agent.visibility, // Use showInLeaderboard from backend
        avatar: agent.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
        badgeType: undefined, // Will be assigned after sorting based on actual position
        // Profile image fields
        profileImageUrl: agent.profileImageUrl,
        profile_image_url: agent.profile_image_url,
        profileImagePath: agent.profileImagePath,
        profile_image_path: agent.profile_image_path,
        // Real KPI metrics from backend
        totalLeads: agent.totalLeads || 0,
        avgCallDuration: agent.avgCallDuration || 0,
        joinDate: agent.joinDate || new Date().toISOString(),
        isOnline: agent.isOnline ?? getStableRandom(agent.userId, 3) > 0.3, // Use backend value if available, fallback to stable random
      }));
      // Backend already filters by agency, no need for frontend filtering

    // Sort by the selected metric to find TRUE top performers - create new array instead of mutating
    const sortedAgents = [...mappedAgents].sort((a, b) => {
      const metricKey = apiMetric as keyof typeof a;
      const aValue = typeof a[metricKey] === 'number' ? a[metricKey] : 0;
      const bValue = typeof b[metricKey] === 'number' ? b[metricKey] : 0;
      return bValue - aValue; // Descending: highest first
    });

    // Assign badges based on position in sorted array for the selected metric
    // This ensures gold/silver/bronze reflect the current metric being viewed
    const agents = sortedAgents.map((agent, index) => ({
      ...agent,
      badgeType: index === 0 ? ('gold' as const) :
                 index === 1 ? ('silver' as const) :
                 index === 2 ? ('bronze' as const) :
                 undefined
    }));

    return agents;
  }, [leaderboardData, apiMetric, userType, userId]); // Include userId for stable random generation

  // Agency-wide mode from API setting OR active god-mode session.
  // From the parent owner's perspective when god-mode is active, the
  // leaderboard IS effectively agency-wide — every sub-agency is in the
  // pool — so the header reads "Agency-Wide Leaderboard" with the same
  // agency-name badges. Difference from real mutual-consent mode is the
  // separate violet "Full Downline" banner above (already rendered),
  // which makes the source of the merge clear.
  // The board shows multiple agencies (so the header reads multi-agency + shows
  // agency-name badges) when the agency-wide setting is on and the viewer hasn't
  // narrowed to just their own agency, or when god-mode is active. The
  // "My Sub-Agencies" view is also multi-agency.
  const isAgencyWide =
    (agencyWideSettingOn && !viewingMyAgencyOnly) ||
    godmodeOn;

  // Collect unique agency names for the header badges
  const agencyWideNames = useMemo(() => {
    if (!isAgencyWide) return [];
    const names = new Set(baseTransformedAgents.map(a => a.subAgency?.name || a.agency?.name).filter(Boolean));
    return [...names].sort();
  }, [isAgencyWide, baseTransformedAgents]);

  // Hall of Champions - ALWAYS shows the true top 3, NOT affected by search
  // This ensures the podium remains static and shows actual top performers
  const topAgents = useMemo(() => {
    // Early return for empty base agents - use stable reference
    if (baseTransformedAgents.length === 0) return EMPTY_ARRAY;

    // Filter to only agents who are visible in leaderboard
    let filteredAgents = baseTransformedAgents.filter(agent => agent.showInLeaderboard);

    // If filter removed nothing, use stable reference
    if (filteredAgents.length === baseTransformedAgents.length) {
      filteredAgents = baseTransformedAgents;
    }

    // If filtered to empty, return stable reference
    if (filteredAgents.length === 0) return EMPTY_ARRAY;

    // DO NOT apply search filter here - Hall of Champions should always show true top 3
    // Search only affects the table below, not the podium

    // IMPORTANT: Show top 3 agents based on the currently selected metric
    // The badges were already assigned correctly in baseTransformedAgents based on sorted position
    // Just take the first 3 from the (already sorted and badged) list
    const result = filteredAgents.slice(0, 3);

    // If result is same as filtered (3 or fewer items), return filtered to avoid new array
    if (result.length === filteredAgents.length) return filteredAgents;
    return result;
  }, [baseTransformedAgents]); // Removed searchQuery dependency - podium should not change with search

  const transformedAgentsForTable = useMemo(() => {
    // Add userId check to prevent errors during initialization
    if (!userId || !leaderboardData?.leaderboard || leaderboardData.leaderboard.length === 0) return EMPTY_ARRAY;

    // Transform API data to table format
    // Use stable hash for random values based on current user's userId + agent's userId to prevent infinite re-renders
    const getStableRandom = (agentUserId: string, seed: number) => {
      let hash = 0;
      const str = agentUserId + userId + seed; // Combine agent ID with current user ID for uniqueness
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash) / 2147483647; // Normalize to 0-1
    };

    const agents = leaderboardData.leaderboard
      // Filter out agents who have opted out of the leaderboard
      .filter(agent => agent.showInLeaderboard ?? agent.visibility)
      .map((agent, index) => ({
        id: agent.userId,
        userId: agent.userId,
        name: agent.name,
        department: agent.department,
        agency: agent.agency, // Pass through agency data
        subAgency: agent.subAgency, // Pass through sub-agency data
        totalPremium: agent.totalPremium,
        issuedPremium: agent.issuedPremium ?? 0,
        totalSales: agent.totalSales,
        totalPolicies: agent.totalPolicies ?? agent.totalSales ?? 0, // Use totalPolicies from backend, fallback to totalSales
        conversionRate: agent.conversionRate,
        wonLeads: agent.wonLeads,
        lostLeads: agent.lostLeads,
        underwritingLeads: agent.underwritingLeads,
        goalProgress: agent.goalProgress ?? null,
        goalLabel: agent.goalLabel ?? null,
        leadsConverted: agent.leadsConverted,
        appointments: agent.appointments,
        callsMade: agent.callsMade,
        activeClients: agent.activeClients,
        visibility: agent.visibility,
        showInLeaderboard: agent.showInLeaderboard ?? agent.visibility, // Use showInLeaderboard from backend
        rank: agent.rank,
        avatar: agent.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
        // Profile image fields
        profileImageUrl: agent.profileImageUrl,
        profile_image_url: agent.profile_image_url,
        profileImagePath: agent.profileImagePath,
        // Real KPI metrics from backend
        totalLeads: agent.totalLeads || 0,
        avgCallDuration: agent.avgCallDuration || 0,
        joinDate: agent.joinDate || new Date().toISOString(),
        isOnline: agent.isOnline ?? getStableRandom(agent.userId, 3) > 0.3, // Use backend value if available, fallback to stable random
        badgeType: undefined, // Will be assigned after sorting based on actual position
      }));

    // Apply search filter with normalized query
    const normalizedSearchQuery = (searchQuery || '').trim();
    let filteredAgents = agents;
    if (normalizedSearchQuery.length > 0) {
      filteredAgents = agents.filter(agent => {
        if (!agent.name || typeof agent.name !== 'string') return false;
        return agent.name.toLowerCase().includes(normalizedSearchQuery.toLowerCase());
      });
    }

    // Return early if no filtering happened (optimization)
    if (filteredAgents === agents && filteredAgents.length === 0) return EMPTY_ARRAY;

    // Re-sort by the selected metric after filtering - create new sorted array instead of mutating
    const sortedAgents = [...filteredAgents].sort((a, b) => {
      const metricKey = apiMetric as keyof typeof a;
      const aValue = typeof a[metricKey] === 'number' ? a[metricKey] : 0;
      const bValue = typeof b[metricKey] === 'number' ? b[metricKey] : 0;
      return bValue - aValue;
    });

    // Assign badges based on position in sorted array for the selected metric
    const sortedWithBadges = sortedAgents.map((agent, index) => ({
      ...agent,
      badgeType: index === 0 ? ('gold' as const) :
                 index === 1 ? ('silver' as const) :
                 index === 2 ? ('bronze' as const) :
                 undefined
    }));

    return sortedWithBadges;
  }, [leaderboardData, searchQuery, apiMetric, userId]); // Include userId for stable random generation

  // FIXED: Stabilize eligibility check to prevent component thrashing
  // Extract primitive value and memoize to prevent conditional render flip-flopping
  const isEligibleForGlobal = useMemo(() => {
    return !currentAgencyId;
  }, [currentAgencyId]);

  const handleMetricChange = useCallback((metric: string) => {
    setSelectedMetric(metric);
  }, []);

  const handlePeriodChange = useCallback((period: string) => {
    setSelectedPeriod(period);
    // Clear custom date range when switching away from custom
    if (period !== 'custom') {
      setCustomDateRange(undefined);
    }
  }, []);

  const handleCustomDateChange = useCallback((dateRange: CustomDateRange | undefined) => {
    setCustomDateRange(dateRange);
  }, []);

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query ?? ''); // Ensure we never set null/undefined
  }, []);

  // Get normalized search query for display
  const normalizedSearchQuery = (searchQuery ?? '').trim();
  const hasSearchQuery = normalizedSearchQuery.length > 0;

  // NOW it's safe to have early returns - all hooks have been called
  // Show loading while user context is initializing or checking agency status
  if (isInitializing || statusLoading || (currentAgencyId && settingsLoading)) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p>Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  // Show loading if user context is not ready
  if (!user || !user.user) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }

  // Show loading if userId is not available yet (needed for stable random generation)
  if (!userId) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }

  // Get agency name for header — use selected agency name from tree if switching, otherwise current agency
  const currentAgencyName = selectedLeaderboardAgencyId && agencyTree
    ? agencyTree.find((a: any) => a.id === selectedLeaderboardAgencyId)?.name || user?.currentAgency?.agency?.name
    : user?.currentAgency?.agency?.name;

  // Check if leaderboard is disabled for agency
  if (currentAgencyId && !isLeaderboardEnabled) {
    return (
      <div className="space-y-8">
        <LeaderboardHeader agencyName={currentAgencyName} isAgencyWide={isAgencyWide} agencyWideNames={agencyWideNames} />
        <Alert className="border-amber-200 bg-amber-50">
          <AlertDescription className="text-amber-800">
            <strong>Leaderboard Disabled</strong>
            <p className="mt-2">
              The leaderboard has been disabled for your agency by the agency owner.
              Please contact your agency owner to enable leaderboard access.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Loading state
  if (loading && !leaderboardData) {
    return (
      <div className="space-y-8">
        <LeaderboardHeader agencyName={currentAgencyId ? currentAgencyName : undefined} isAgencyWide={isAgencyWide} agencyWideNames={agencyWideNames} />
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  // Error state
  if (error && !leaderboardData) {
    return (
      <div className="space-y-8">
        <LeaderboardHeader agencyName={currentAgencyId ? currentAgencyName : undefined} isAgencyWide={isAgencyWide} agencyWideNames={agencyWideNames} />
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load leaderboard data: {error}
            <button
              className="ml-2 underline hover:no-underline"
              onClick={handleRefresh}
            >
              Retry
            </button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Independent users (no agency) can view the GLOBAL INDEPENDENT leaderboard
  // No need to block them with agency creation prompt here - they can still see leaderboard

  // Show admin notice if viewing all agencies (system admin only)
  const isSystemAdmin = userType === 'admin';
  const showAdminNotice = isSystemAdmin && !currentAgencyId;

  // Show agency filtering notice for non-admin users
  const showAgencyFilterNotice = !isSystemAdmin && currentAgencyId;

  // Render agency leaderboard content
  const agencyLeaderboardContent = (
    <>
        {/* Hidden Status Banner - Show when user has opted out */}
        {!userOptedIn && (
          <Alert className="bg-amber-50 border-amber-200">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <EyeOffIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="text-amber-800">
                  <strong>You are hidden from the leaderboard.</strong>
                  <span className="ml-1">Your stats are not visible to other agents.</span>
                </div>
              </div>
              <Link
                href={
                  // Shell-aware: this page is also embedded at /m/leaderboard
                  typeof window !== "undefined" && window.location.pathname.startsWith("/m")
                    ? "/m/settings"
                    : "/settings?tab=account&accountTab=preferences"
                }
              >
                <Button variant="outline" size="sm" className="ml-4 border-amber-300 hover:bg-amber-100 flex-shrink-0">
                  <Settings className="h-4 w-4 mr-2" />
                  Change in Settings
                </Button>
              </Link>
            </div>
          </Alert>
        )}

        {/* Admin Notice - Show when system admin is viewing all agencies */}
        {showAdminNotice && (
          <Alert className="border-blue-200 bg-blue-50">
            <AlertDescription className="text-blue-800">
              <strong>System Admin View:</strong> You are viewing leaderboard data from all agencies.
              {user.currentAgency?.agency?.name && ` Currently selected agency: ${user.currentAgency.agency.name}`}
            </AlertDescription>
          </Alert>
        )}

        {/* Hall of Champions - Always shows true top 3 performers (not affected by search) */}
        {topAgents.length > 0 ? (
          <div className={`transition-opacity duration-200 ${isRefreshing ? "opacity-60" : "opacity-100"}`}>
            <HeroPodium topAgents={topAgents} selectedMetric={selectedMetric} />
          </div>
        ) : hasSearchQuery ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-gray-600 mb-2">No Champions Found</h2>
            <p className="text-gray-500">
              No agents match your search "{normalizedSearchQuery}". Try a different search term.
            </p>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👥</div>
            <h2 className="text-2xl font-bold text-gray-600 mb-2">No Champions Available</h2>
            <p className="text-gray-500">
              No agents are currently participating in the leaderboard.
            </p>
          </div>
        )}

        {/* Share your win — agent rank card, plus the owner's top-3 podium.
            RE-ENABLED 2026-08-11: rebuilt on the sales-win design system
            (rankwin/topboard templates, modeled on @thefoundationfinancial's
            IG top-producer posts) with the same dialog flow as team chat.
            The BUTTON renders inside AdvancedLeaderboardFilters next to Export
            (via onShare) — both "take this off the board" actions in one row. */}
        <ShareWinDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          me={shareMe}
          top3={shareTop3}
          periodLabel={sharePeriodLabel}
          agencyId={effectiveAgencyId}
        />

        {/* Advanced Filters */}
        {/* Content fades slightly while a new filter's data lands. Previous
            results stay on screen (placeholderData), so switching metric or
            period reads as a swap rather than a page reload. */}
        <AdvancedLeaderboardFilters
          leaderboardData={leaderboardData}
          loading={loading}
          onMetricChange={handleMetricChange}
          onPeriodChange={handlePeriodChange}
          onRefresh={handleRefresh}
          onSearch={handleSearch}
          onShare={shareMe || shareTop3 ? () => setShareOpen(true) : undefined}
          searchQuery={searchQuery} // Pass the current search query to keep in sync
          selectedMetric={selectedMetric}
          selectedPeriod={selectedPeriod}
          customDateRange={customDateRange}
          onCustomDateChange={handleCustomDateChange}
          agencies={agencyTree}
          selectedAgencyId={effectiveAgencyId}
          onAgencyChange={setSelectedLeaderboardAgencyId}
          showAgencySwitcher={canSwitchAgencies}
          // Scope-picker props (owner-only, persists to backend)
          isAgencyOwner={isAgencyOwner}
          ownerAgencyId={currentAgencyId}
          ownerAgencyName={user?.currentAgency?.agency?.name}
          // Parent god-mode session controls — only exposes the option when
          // the Settings toggle is on for this agency.
          parentGodmodeAvailable={parentGodmodeAvailable}
          isGodmodeActive={isGodmodeActive}
          onGodmodeChange={(active) => {
            setIsGodmodeActive(active);
            // Re-fetch immediately on switch so the user sees the new pool
            // without waiting for the next 60s poll.
            void manualRefresh();
          }}
          onScopeSaved={() => {
            // Three caches to bust so the new scope applies immediately:
            //   1. agency-wide-settings (5min staleTime) — drives the
            //      "Agency-Wide Leaderboard" header label
            //   2. agency-leaderboard-settings — sidebar visibility cache
            //   3. backend leaderboard data cache (60s TTL) — bypassed by
            //      manualRefresh which sets forceFresh=true
            // Without #1 the header wouldn't flip to "Agency-Wide" until the
            // 5-min staleTime expired even though the data was already correct.
            queryClient.invalidateQueries({ queryKey: ['agency-wide-settings', currentAgencyId] });
            queryClient.invalidateQueries({ queryKey: ['agency-leaderboard-settings', currentAgencyId] });
            void manualRefresh();
          }}
        />

        {/* Performance Cards - Uses unfiltered data for overall stats */}
        <div className={`transition-opacity duration-200 ${isRefreshing ? "opacity-60" : "opacity-100"}`}>
        <PerformanceCards 
          agents={baseTransformedAgents}
          period={selectedPeriod}
          stats={leaderboardData?.stats}
        />

        {/* Leaderboard Table - Uses search-filtered data */}
        <SimpleLeaderboardTable
          agents={transformedAgentsForTable}
          loading={loading}
          metric={selectedMetric}
          onVisibilityChange={updateVisibility}
          period={selectedPeriod}
          rawData={leaderboardData}
          searchQuery={searchQuery}
          stats={leaderboardData?.stats}
        />
        </div>
    </>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <LeaderboardHeader agencyName={currentAgencyId ? currentAgencyName : undefined} isAgencyWide={isAgencyWide} agencyWideNames={agencyWideNames} />

      {/* Personal scope picker. Only shown when the parent enabled agency-wide
          (otherwise everyone already sees just their own agency). Lets each
          person narrow what THEY see — it never changes the setting for anyone
          else. Owners with sub-agencies also get a "My Sub-Agencies" option. */}
      {canChooseScope && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
          <span className="text-sm font-medium text-gray-700">View:</span>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              type="button"
              onClick={() => setViewScope('mine')}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                effectiveScope === 'mine'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              My Agency
            </button>
            {showDownlineOption && (
              <button
                type="button"
                onClick={() => setViewScope('downline')}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                  effectiveScope === 'downline'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                My Sub-Agencies
              </button>
            )}
            <button
              type="button"
              onClick={() => setViewScope('wide')}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                effectiveScope === 'wide'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Agency-Wide
            </button>
          </div>
          <span className="text-xs text-gray-500">
            {effectiveScope === 'mine'
              ? 'Showing just your agency’s agents.'
              : effectiveScope === 'downline'
                ? 'Showing your agency and everything below it.'
                : 'Showing every agency in the agency-wide pool.'}
          </span>
        </div>
      )}

      {/* God-mode active banner — only renders when the owner has the
          parent_godmode_enabled flag AND has switched into the Full
          Downline view in this session. Persistent visual reminder so they
          don't forget they're looking at a privileged view. */}
      {parentGodmodeAvailable && isGodmodeActive && (
        <div className="flex items-center gap-3 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-violet-900">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-violet-600 text-white text-xs font-bold">👁</span>
          <div className="flex-1 text-sm">
            <span className="font-semibold">Full Downline View</span> — you're seeing every sub-agency below you regardless of their consent, plus every agent including ones who hid themselves. Visible only to you. This view is logged.
          </div>
          <button
            type="button"
            onClick={() => {
              setIsGodmodeActive(false);
              void manualRefresh();
            }}
            className="rounded-md border border-violet-300 bg-white px-3 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-100"
          >
            Exit Full Downline
          </button>
        </div>
      )}

      {/* Freshness indicator + manual refresh. Backend caches at 60s TTL. The
          Refresh button bypasses the cache once, then is disabled for 10s to
          match the server-side rate limit (no spam-clicks allowed). */}
      <LeaderboardFreshness cachedAt={cachedAt} canRefresh={canRefresh} onRefresh={manualRefresh} loading={loading} />

      {/* Same layout for both agency and global leaderboards. The owner-only
          scope picker (Just my agency / Agency-wide / Custom) is folded into
          the existing Agency dropdown inside AdvancedLeaderboardFilters. */}
      {agencyLeaderboardContent}
    </div>
  );
};

export default AgentLeaderboard;

/** Shows "Last updated: 23s ago" + a refresh button that can't be spammed. */
function LeaderboardFreshness({
  cachedAt,
  canRefresh,
  onRefresh,
  loading,
}: {
  cachedAt: string | undefined;
  canRefresh: boolean;
  onRefresh: () => Promise<void>;
  loading: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Tick every second so the "Just now / Xs ago" label counts in real time.
    // The timer is cheap (only re-renders this small component — the leaderboard
    // table above is memoized, so no downstream re-renders).
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);
  const ageMs = cachedAt ? now - new Date(cachedAt).getTime() : null;
  const ageLabel = ageMs == null
    ? 'Loading…'
    : ageMs < 15_000 ? 'Just now'
    : ageMs < 60_000 ? `${Math.floor(ageMs / 1000)}s ago`
    : ageMs < 3_600_000 ? `${Math.floor(ageMs / 60_000)}m ago`
    : 'Over an hour ago';
  const disabled = loading || !canRefresh;
  return (
    <div className="flex items-center justify-end gap-3 -mt-4 text-xs text-muted-foreground">
      <span>Last updated: <span className="font-medium text-foreground">{ageLabel}</span></span>
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={() => void onRefresh()}
        className="h-7 px-2 gap-1.5"
        aria-label="Refresh leaderboard"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        <span>Refresh</span>
      </Button>
    </div>
  );
}